import React, {useState} from 'react'
import {Link as RouterLink, useNavigate} from 'react-router-dom'
import ChangeIcon from '../../../images/icons/new-dashboard/reload-icon.svg'
import ChangeIconHover from '../../../images/icons/new-dashboard/reload-icon-white.svg'
import InformationIcon from '../../../images/icons/new-dashboard/information-icon.svg'
import CompleteIcon from '../../../images/icons/new-dashboard/complete-icon.svg'
import {
  ChangeMode,
  CompletedText,
  GuaranteeBox,
  GuaranteeText,
  ProgressBarBox,
  ProgressBox,
  ProgressFlexBox,
  StyledIcon,
  SubTitle,
  Title,
  TitleFlexBox,
  TooltipBox,
  WhiteTooltip,
} from './styles'
import {Button} from '../../../components/button'
import {LinearProgress} from '@mui/material'
import StudyPlanDialog from '../Dialogs/StudyPlanDialog'
import {Divisions, getDivisionDetails} from '../../../constants/divisions'
import {Divider} from '../Cards/BaseCard/styles'
import {useWindowDimensions} from '../../../hooks/useWindowDimensions'
import {StudyGuide} from '../../../redux/studyGuides'
import {Attempt} from '../../../redux/reportExamStatus'
import {LinkBox} from '../../../styles/global'
import {useSelector} from 'react-redux'
import {selectMemberAccess} from '../../../redux/members'
import {useAuth} from '../../../components/AuthProvider'
import {useAuth0} from '@auth0/auth0-react'
import GuaranteeLock from '../../../images/icons/new-dashboard/guarantee-lock.svg'
import GuaranteeCheck from '../../../images/icons/new-dashboard/guarantee-check.svg'

type Props = {
  division: Divisions
  courseId?: string
  studyGuides?: StudyGuide[]
  source?: 'card' | 'page'
  reportStatus?: Attempt
}

const View = ({
  division,
  courseId,
  studyGuides,
  reportStatus,
  source,
}: Props) => {
  const auth = useAuth()
  const {loginWithPopup} = useAuth0()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [buttonHover, setButtonHover] = useState(false)

  const {isMobile} = useWindowDimensions()

  const navigate = useNavigate()

  const divisionDetails = getDivisionDetails(division)

  const access = useSelector(selectMemberAccess)

  const hasAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.study_guides?.value) === 1
      : false

  const isCard = source === 'card'

  const studyGuide = studyGuides?.find(sg => sg.inProgress)

  const handleClick = () => {
    if (!studyGuide?.inProgress) {
      setIsDialogOpen(true)
    } else {
      if (studyGuide && studyGuide.inProgress) {
        navigate(
          studyGuide.timeWindow > 0
            ? `/study-guide/${studyGuide.id}`
            : `/profile/dashboard/${studyGuide.courseId}`,
        )
      }
    }
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  const progress =
    studyGuide && studyGuide.completeDays
      ? (studyGuide.completeDays / studyGuide.timeWindow) * 100
      : 0

  const isDivisionComplete = reportStatus?.passed ? true : false

  const courseUrl =
    studyGuide?.timeWindow === 0 || !studyGuide
      ? `/profile/dashboard/${courseId}`
      : `/study-guide/${studyGuide?.id}`

  return (
    <>
      <ProgressBox $isCard={isCard}>
        <ProgressFlexBox
          onClick={() =>
            isMobile && !studyGuide?.inProgress ? handleClick() : {}
          }
        >
          <TitleFlexBox $isCard={isCard}>
            <StyledIcon
              $isDivisionComplete={isDivisionComplete}
              src={divisionDetails?.icon}
              alt="division"
            />
            <Title $isDivisionComplete={isDivisionComplete}>
              {divisionDetails?.name}
            </Title>
          </TitleFlexBox>
          {isCard && <Divider />}
          {studyGuide?.inProgress && (
            <>
              <SubTitle $isCard={isCard}>
                {studyGuide.timeWindow > 0 ? (
                  <>Guided Path: {studyGuide.timeWindow} Day Study Plan</>
                ) : (
                  <>Flexible Study</>
                )}
              </SubTitle>
              {studyGuide.timeWindow > 0 && (
                <GuaranteeBox>
                  <WhiteTooltip
                    title={
                      <TooltipBox>
                        <b>Guarantee</b>
                        <span>
                          Purchase an Expert subscription, complete the entire
                          course, and in the unlikely event you don't pass,
                          we'll contact NCARB to pay for your retake.{' '}
                          <a
                            href="https://www.blackspectacles.com/guarantee"
                            target="_blank"
                          >
                            Pass the ARE Guarantee.
                          </a>
                        </span>
                      </TooltipBox>
                    }
                    arrow
                  >
                    <GuaranteeText>
                      <img
                        src={
                          studyGuide.completeDays === studyGuide.timeWindow
                            ? GuaranteeCheck
                            : GuaranteeLock
                        }
                        alt="Guarantee Claim"
                      />
                      Guarantee{' '}
                      <img src={InformationIcon} alt="Guarantee Information" />
                    </GuaranteeText>
                  </WhiteTooltip>
                </GuaranteeBox>
              )}
              {studyGuide.timeWindow > 0 && (
                <ProgressBarBox $isCard={isCard}>
                  <LinearProgress variant="determinate" value={progress ?? 0} />{' '}
                  {studyGuide.completeDays}/{studyGuide.timeWindow} days
                </ProgressBarBox>
              )}
              {(hasAccess || (!hasAccess && studyGuide.timeWindow > 0)) && (
                <>
                  {isCard ? (
                    <div
                      onMouseEnter={() => setButtonHover(true)}
                      onMouseLeave={() => setButtonHover(false)}
                    >
                      <Button
                        color="primary"
                        children={
                          <span style={{display: 'flex', gap: '8px'}}>
                            <img
                              src={buttonHover ? ChangeIconHover : ChangeIcon}
                              alt="Change Study Mode"
                            />
                            Change Study Mode
                          </span>
                        }
                        onClick={() => setIsDialogOpen(true)}
                        style={{width: '100%', marginTop: '16px'}}
                      />
                    </div>
                  ) : (
                    <ChangeMode onClick={() => setIsDialogOpen(true)}>
                      Change Study Mode{' '}
                      <img src={ChangeIcon} alt="Change Study Mode" />
                    </ChangeMode>
                  )}
                </>
              )}
            </>
          )}
        </ProgressFlexBox>
        {!auth.isAuthenticated ? (
          <Button
            color="secondary"
            children={'Login'}
            onClick={() =>
              loginWithPopup(
                {
                  authorizationParams: {
                    redirect_uri: window.location.origin,
                  },
                },
                {
                  timeoutInSeconds: 360,
                },
              )
            }
          />
        ) : (
          <>
            {!isCard && ((isMobile && studyGuide?.inProgress) || !isMobile) && (
              <>
                {isDivisionComplete ? (
                  <LinkBox to={courseUrl} component={RouterLink}>
                    <CompletedText>
                      <img src={CompleteIcon} alt="Completed" /> Exam passed
                    </CompletedText>
                  </LinkBox>
                ) : (
                  <Button
                    color={studyGuide?.inProgress ? 'secondary' : 'primary'}
                    children={
                      studyGuide?.inProgress ? 'Resume' : 'Launch Course'
                    }
                    onClick={handleClick}
                  />
                )}
              </>
            )}
          </>
        )}
        <StudyPlanDialog
          open={isDialogOpen}
          setOpen={handleCloseDialog}
          studyGuides={studyGuides}
          reportStatus={reportStatus}
          studyGuide={studyGuide}
          isCard={isCard}
        />
      </ProgressBox>
    </>
  )
}

export default View
